import { Component, Inject, ElementRef, ViewChild, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { FerrerAuthService } from 'app/services/authentication.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DialogUtilsService } from 'app/modules/utils/dialog-utility.service';
import { OtpSuggestDialogComponent } from '../otp-suggest/otpsuggestpwd-dialog.component';
import { ConsentDialogComponent } from '../consent-dialog/consent-dialog.component';
import { BlockedMedicalComponent } from 'app/modules/register-steps/non-medical/non_medical.component';
import { BlockedUserComponent } from 'app/modules/register-steps/blocked-user/blocked_user.component';
import { DiffCountryDialogComponent } from '../diffcountry-dialog/diffcountry-dialog.component';
import { NavigationFlowService } from 'app/services/navigation-flow.service';
import { firstValueFrom } from 'rxjs';
import { EmailVerifyStepComponent } from 'app/modules/register-steps/email-verify-window/email_verify_window.component';
@Component({
  selector: 'app-otp-dialog',
  templateUrl: './otp-dialog.component.html',
  imports: [
    FormsModule,
    CommonModule,
    TranslocoPipe,
    MatIconModule,
    MatProgressSpinnerModule
  ],
})
export class OtpDialogComponent {
  code: string[] = Array(6).fill('');
  buttonDisabled: boolean = true;
  inputFieldsError: boolean = false;
  errorMessage: string = '';
  otpApiError: string | null = null;
  otpApiResendEmailError: string | null = null;
  isLoading: boolean = false;
  email: string;


  @ViewChildren('codeInput') inputs!: QueryList<ElementRef>;
  constructor(private dialogRef: MatDialogRef<OtpDialogComponent>,
    private http: HttpClient,
    private translocoService: TranslocoService,
    private _dialogRef: MatDialogRef<any>,
    private _dialog: MatDialog,
    private ferrerAuth: FerrerAuthService,
    private dialogUtilsService: DialogUtilsService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private navigationFlow: NavigationFlowService
  ) { }



  ngOnInit(): void {

    if (this.data) {
      this.email = this.data.email;
    } else {
      const userEmail = localStorage.getItem('userMail');
      if (userEmail) {
        this.email = userEmail;
      }
    }
    this.ferrerAuth.sendOtpCode(this.email).subscribe({
      next: (response) => {
        console.log('OTP code sent successfully:', response);
        // Continue with dialog opening only after successful OTP request

      },
      error: (error) => {
        console.error('Failed to send OTP code:', error);
        this.handleApiError(error);
        // We don't show errors to user in fire-and-forget scenarios
      }
    });
  }
  ngAfterViewInit(): void {
    const first = this.inputs.first;
    if (first) {
      first.nativeElement.focus();
    }
  }

  changeLang(lang: string) {
    this.translocoService.setActiveLang(lang);
  }


  handleInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/[^a-zA-Z0-9]/g, '');  // Strip non-alphanumeric characters

    // Update the model only if there is a value
    if (value) {
      this.code[index] = value[0].toUpperCase();
      input.value = this.code[index]; // Ensure the input displays the processed character
    } else {
      this.code[index] = '';
    }

    this.updateButtonState();  // Update button state based on validity

    // Move to the next field if the current one is filled
    if (value && index < this.code.length - 1) {
      setTimeout(() => this.focusInput(index + 1), 0);
    }
  }

  handleKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      // If the input is empty and backspace is pressed, move to the previous input
      if (!input.value && index > 0) {
        this.focusInput(index - 1);
      }
    }
    this.updateButtonState();

  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
  focusInput(index: number): void {
    const inputsArr = this.inputs.toArray();
    if (inputsArr[index]) {
      const el = inputsArr[index].nativeElement;
      el.focus();
      el.select(); // ensures replacement instead of append
    }
  }


  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text') || '';
    // Replace with alphanumeric filter instead of digits-only
    const characters = pasteData.replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .split('')
      .slice(0, this.code.length);

    characters.forEach((char, idx) => {
      this.code[idx] = char;

      // Update the input field values for visual consistency
      const inputsArr = this.inputs.toArray();
      if (inputsArr[idx]) {
        inputsArr[idx].nativeElement.value = char;
      }
    });

    this.updateButtonState();

    // Move focus to next empty input
    const nextIndex = characters.length < this.code.length ? characters.length : this.code.length - 1;
    this.focusInput(nextIndex);
  }


  hasGlobalError(): boolean {
    // Update regex to check for alphanumeric characters
    return !this.code.every(c => /^[a-zA-Z0-9]$/i.test(c));
  }

  updateButtonState(): void {
    const isValid = !this.hasGlobalError();
    this.buttonDisabled = !isValid;
  }

  formatCode(): string {
    return this.code.join('').toUpperCase();
  }
  submitCode(): void {
    //submit code and login
    if (this.hasGlobalError()) {

      return;
    }

    this.validateInputs(this.code);
    if (this.inputFieldsError) return;

    this.otpApiError = '';
    this.otpApiResendEmailError = '';
    this.isLoading = true;

    // Combine and format the OTP characters
    const otp = this.formatCode();
    this.ferrerAuth.login(this.email, undefined, otp)
      .subscribe({
        next: (response) => {
          this.isLoading = false; // Hide loading spinner or enable button

          if (response && response.userAccess?.termsAccepted === false) {
            const currentCountry = JSON.parse(localStorage.getItem('currentCountryInfo'));
            if (response.userAccess?.country !== currentCountry.country) {
              this.opendiffCountryDialog(response);
            } else {
              this.openConsentsDialog(response);
              this.dialogRef.close("response");
            }

            return;
          }
          if (response && response.userAccess?.success === true) {
            this.openpwdChangeDialog(response);
            this.dialogRef.close("response");
          } else {
            this.isLoading = false;

            this.handleApiError(response);
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.handleApiError(err);
        }
      });
  }

  handleApiError(error: any): void {

    // Get error response body
    const errorCode = error.code || error.error.code || '';
    const errorBody = error.error.message || error.error || error;
    if (errorBody.includes('user has not accepted all consents') ||
      errorBody.includes('consents not accepted')) {
      this.openConsentsDialog();
      return;
    }
    if (errorBody.includes('Prohibido el acceso')) {
      this.openBlockedDialog();
      return;
    }

    if (errorBody.includes('LOGIN_REQUEST_WAS_NOT_VERIFIED_HCA') || errorBody.includes('LOGIN_REQUEST_HCA_RESULT_WAS_NO_MEDICAL_PROFESSIONAL') || errorCode.includes('LOGIN_REQUEST_HCA_RESULT_WAS_NO_MEDICAL_PROFESSIONAL')) {
      this.openBlockedHCADialog();
      return;
    }
    if (errorBody.includes('User OTP code not found in system') || errorBody.includes('OTP_CODE_NOT_FOUND')) {
      this.otpApiError = this.translocoService.translate('errors.otpCodeNotFound');
      return;
    }
    if (errorBody.includes('OTP_USER_EMAIL_EXCEEDED_REQUEST_LIMIT') || errorBody.includes('exceeded OTP request limit')) {
      //doesnt exist
      this.otpApiError = this.translocoService.translate('errors.otpRequestLimitExceeded');
      return;
    }

    if (errorCode.includes('OTP_USER_EMAIL_NOT_FOUND')) {
      this.otpApiError = this.translocoService.translate('errors.emailNotFound');
      return;
    }

    // Recuerda que debes activar tu cuenta.
    // TODO: add a specific error code for this case in backend, remove hardcoded string

    if (errorBody.includes('Recuerda que debes activar tu cuenta.')) {
      this.otpApiResendEmailError = errorBody;
      return;
    }

    this.otpApiError = errorBody;

  }
  resendCode(): void {
    this.code = Array(6).fill('');

    // Reset error states
    this.inputFieldsError = false;
    this.errorMessage = '';
    this.otpApiError = null;
    this.otpApiResendEmailError = null;

    // Clear the actual DOM input elements
    setTimeout(() => {
      const inputsArr = this.inputs.toArray();
      inputsArr.forEach(input => {
        input.nativeElement.value = '';
      });

      // Focus on the first input field
      if (inputsArr.length > 0) {
        inputsArr[0].nativeElement.focus();
      }
    });

    // Disable the submit button
    this.buttonDisabled = true;

    this.ferrerAuth.sendOtpCode(this.email).subscribe({
      next: (response) => {

      },
      error: (error) => {
        this.handleApiError(error);
      }
    });

  }

  openEmailVerifiedDialog(): void {
    if (this._dialogRef) {
      // Close without animation
      this._dialogRef.close();
    }

    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(EmailVerifyStepComponent, {
      width: `${width}px`,
      //height: `${height}px`,
      maxWidth: '90vw',
      maxHeight: '65vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      // Disable enter animation
      enterAnimationDuration: '0ms',
      // Disable exit animation
      exitAnimationDuration: '0ms',
      data: {
        email: this.email
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }

  closeDialog(): void {
    console.log('[OTP-DIALOG] closeDialog called, calling dialogUtilsService');
    this.dialogUtilsService.closeDialog(this.dialogRef);
    console.log('[OTP-DIALOG] dialogUtilsService.closeDialog completed');
  }

  validateInputs(inputs: string[]) {
    const allFilled = inputs.every((val) => val.trim() !== '');
    // Replace with alphanumeric validation
    const allSingleChar = inputs.every((val) => /^[a-zA-Z0-9]$/i.test(val));

    if (!allFilled) {
      this.inputFieldsError = true;
      this.errorMessage = 'Todos los campos deben estar completos';
    } else if (!allSingleChar) {
      this.inputFieldsError = true;
      this.errorMessage = 'Todos los campos deben contener un solo carácter alfanumérico';
    } else {
      this.inputFieldsError = false;
      this.errorMessage = '';
    }
  }
  async backLoginDialog(): Promise<void> {
    // Use NavigationFlowService to transition from OTP to login dialog
    // This will close OTP and open login with proper transition
    await firstValueFrom(this.navigationFlow.transitionOtpToLogin());
  }
  openpwdChangeDialog(response?: any): void {
    if (this._dialogRef) {
      this._dialogRef.close();
    }

    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(OtpSuggestDialogComponent, {
      width: `${width}px`,
      //height: `${height}px`,
      maxWidth: '90vw',
      maxHeight: '75vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      data: {
        apiResponse: response,
        email: this.email
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }


  openConsentsDialog(response?: any): void {
    if (this._dialogRef) {
      this._dialogRef.close();
    }
    const otpCode = this.formatCode();
    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(ConsentDialogComponent, {
      width: `${width}px`,
      //height: `${height-50}px`,
      maxWidth: '90vw',
      maxHeight: '85vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      data: {
        email: this.email,
        otpCode: otpCode,
        loginaction: 'otpLogin',
        consentsAceptados: response.consents,
        userCountry: response.country || response.userAccess?.country
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }

  opendiffCountryDialog(response: any): void {
    // Close any existing dialog
    if (this._dialogRef) {
      this._dialogRef.close();
    }
    const otpCode = this.formatCode();
    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(DiffCountryDialogComponent, {
      width: `${width}px`,
      //height: `${height}px`,
      maxWidth: '90vw',
      maxHeight: '65vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      data: {
        email: this.email,
        otpCode: otpCode,
        loginaction: "passwordLogin",
        consentsAceptados: response.consents,
        userCountry: response.country
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }
  openBlockedDialog(): void {
    // Close any existing dialog
    const otpCode = this.formatCode();
    if (this._dialogRef) {
      this._dialogRef.close();
    }

    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(BlockedUserComponent, {
      width: `${width}px`,
      //height: `${height}px`,
      maxWidth: '90vw',
      maxHeight: '45vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      data: {
        email: this.email,
        otpCode: otpCode,
        loginaction: 'otpLogin'
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }
  shouldShowCloseButton(): boolean {
    return true;
    const apiVersion = JSON.parse(localStorage.getItem('ferrerSessionData'));
    return this.dialogUtilsService.shouldShowCloseButton();
  }
  openBlockedHCADialog(): void {
    // Close any existing dialog
    const otpCode = this.formatCode();
    if (this._dialogRef) {
      this._dialogRef.close();
    }

    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(BlockedMedicalComponent, {
      width: `${width}px`,
      //height: `${height}px`,
      maxWidth: '90vw',
      maxHeight: '45vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      data: {
        email: this.email,
        otpCode: otpCode,
        loginaction: 'otpLogin'
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }
}
